import { Injectable, Type } from '@angular/core';
import {
  D4ActiveTasksListWidgetComponent,
  D4ActiveTasksListWidgetConfigurationComponent,
  PowerBiWidgetComponent,
  PowerBiWidgetConfigurationComponent,
  ServiceRequestListWidgetComponent,
  ServiceRequestListWidgetConfigurationComponent,
} from './components/widgets';
import { D4ActiveTaskListWidgetConfiguration } from './models/d4-active-tasks-list-widget.model';
import { PowerBiWidgetConfiguration } from './models/powerbi-widget.model';
import { ServiceRequestListWidgetConfiguration } from './models/service-request-list-widget.model';
import {
  WidgetComponent,
  WidgetConfiguration,
  WidgetConfigurationComponent,
  WidgetConfigurationType,
  WidgetType,
} from './models/widget-configuration.model';

export interface WidgetDescriptor {
  type: WidgetType;
  label: string;
  component: Type<WidgetComponent>;
  configurationComponent: Type<WidgetConfigurationComponent>;
  createDefaultConfig: () => WidgetConfigurationType;
  validator: (config: WidgetConfigurationType) => boolean;
  defaultSize: { cols: number; rows: number; minItemCols: number; minItemRows: number };
  guideLink?: string;
}

@Injectable({ providedIn: 'root' })
export class WidgetRegistry {
  private registry = new Map<WidgetType, WidgetDescriptor>();

  constructor() {
    this.register({
      type: 'd4-active-tasks',
      label: $localize`D4 active tasks`,
      component: D4ActiveTasksListWidgetComponent,
      configurationComponent: D4ActiveTasksListWidgetConfigurationComponent,
      createDefaultConfig: () => ({ reloadDelay: 5 }),
      validator: (config: WidgetConfigurationType): boolean => {
        const d4Config: D4ActiveTaskListWidgetConfiguration =
          config as D4ActiveTaskListWidgetConfiguration;
        return !!(d4Config.reloadDelay && d4Config.reloadDelay >= 1);
      },
      defaultSize: { cols: 8, rows: 8, minItemCols: 8, minItemRows: 8 },
      guideLink: undefined
    });

    this.register({
      type: 'user-service-request-widget',
      label: $localize`Service requests`,
      component: ServiceRequestListWidgetComponent,
      configurationComponent: ServiceRequestListWidgetConfigurationComponent,
      createDefaultConfig: (): ServiceRequestListWidgetConfiguration => ({
        header: $localize`My Service Requests`,
        reloadDelay: 5,
        searchTerm: '',
      }),
      validator: (config: WidgetConfigurationType): boolean => {
        const serviceConfig: ServiceRequestListWidgetConfiguration =
          config as ServiceRequestListWidgetConfiguration;
        return !!(
          serviceConfig.header &&
          serviceConfig.reloadDelay &&
          serviceConfig.reloadDelay >= 1
        );
      },
      defaultSize: { cols: 10, rows: 8, minItemCols: 8, minItemRows: 8 },
      guideLink: undefined
    });

    this.register({
      type: 'powerbi-widget',
      label: $localize`Power BI widget`,
      component: PowerBiWidgetComponent,
      configurationComponent: PowerBiWidgetConfigurationComponent,
      createDefaultConfig: (): PowerBiWidgetConfiguration => ({
        header: $localize`My Powerbi widget`,
        url: '',
      }),
      validator: (config: WidgetConfigurationType): boolean => {
        const powerBiConfig: PowerBiWidgetConfiguration = config as PowerBiWidgetConfiguration;
        return !!(powerBiConfig.header && powerBiConfig.url);
      },
      defaultSize: { cols: 10, rows: 8, minItemCols: 8, minItemRows: 8 },
      guideLink: 'https://2care4dk.sharepoint.com/sites/IT-Site/SitePages/carehub_modules_utilities_power-bi-widget.aspx'
    });
  }

  register(descriptor: WidgetDescriptor): void {
    this.registry.set(descriptor.type, descriptor);
  }

  getDescriptor(type: WidgetType): WidgetDescriptor {
    const descriptor: WidgetDescriptor | undefined = this.registry.get(type);
    if (!descriptor) {
      throw new Error(`No widget registered for type: ${type}`);
    }
    return descriptor;
  }

  getAvailableWidgets(): { label: string; value: WidgetType }[] {
    return Array.from(this.registry.values()).map(desc => ({
      label: desc.label,
      value: desc.type,
    }));
  }

  getComponent(type: WidgetType): Type<WidgetComponent> | undefined {
    return this.getDescriptor(type).component;
  }

  getConfigurationComponent(type: WidgetType): Type<WidgetConfigurationComponent> {
    return this.getDescriptor(type).configurationComponent;
  }

  createDefaultConfig(type: WidgetType): WidgetConfigurationType {
    return this.getDescriptor(type).createDefaultConfig();
  }

  getDefaultSize(type: WidgetType): { cols: number; rows: number } {
    return this.getDescriptor(type).defaultSize;
  }

  getGuideLink(type: WidgetType): string | undefined {
    return this.getDescriptor(type).guideLink;
  }

  isValid(configuration: WidgetConfiguration): boolean {
    const validator: (config: WidgetConfigurationType) => boolean = this.getDescriptor(
      configuration.type
    ).validator;
    return validator(configuration.configuration);
  }
}
